
import Vue from 'vue';
import { annotationSourceReference } from '@/utils/annotationSource';
import { mapActions, mapState } from 'vuex';
import { NODE_RUN_RESULT_SELECTION_MODE } from '@/views/Runnits/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import RunnitDynamicFieldLabelRow from '@/views/Runnits/RunnitSettings/RunnitDynamicFieldLabelRow.vue';
import {
	RunnitNodeRunInputValue,
	RunnitNodeRunResult,
	RunnitUpload,
	UploadFile,
	IMAGE_UPLOAD_MODE
} from '@run-diffusion/shared';
import _cloneDeep from 'lodash/cloneDeep';
import MultiImageInputSelectedImageContainer from '@/views/Runnits/RunnitSettings/ImageInput/MultiImageInputSelectedImageContainer.vue';
import { IMAGE_GALLERY_DIALOG_NAV, ImageGalleryDialogNav } from '@/components/ImageGallery/utils';
import { RunnitDynamicFieldMixin } from '@/views/Runnits/RunnitSettings/mixins/RunnitDynamicFieldMixin';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import VisualPromptEditor from '@/views/Runnits/RunnitSettings/VisualPromptEditor/VisualPromptEditor.vue';
import Pose3DEditor from '@/views/Runnits/RunnitSettings/Pose3DEditor/Pose3DEditor.vue';
import { fetchRunnitImageSrc } from '@/utils/fetchRunnitImageSrc';
import type { ImgsVisualPromptReplaceTarget } from '@/utils/imgsVisualPromptTarget';
import {
	buildOriginalImagesMap,
	originalEntryForTarget,
	originalImageSourceFromEntry,
	replaceImgsValueAtTarget,
	resetImgsValueAtTarget,
} from '@/utils/imgsVisualPromptTarget';

export default Vue.extend({
	name: 'MultiImageInput',
	mixins: [
		RunnitDynamicFieldMixin,
		RunnitsImageSelectMixin,
	],
	props: {
		field: { type: Object, required: true },
		value: { type: Object, default: null },
		nodeId: { type: String, default: null },
		adminOnly: { type: Boolean, default: false },
	},
	data () {
		return {
			IMAGE_UPLOAD_MODE,
			IMAGE_GALLERY_DIALOG_NAV,
			// Option B UX: while selecting from left, keep committing disabled
			selectFromLeftSessionActive: false,
			lastValidSelectFromLeft: {
				uploads: [] as RunnitUpload[],
				nodeRunResults: [] as RunnitNodeRunResult[],
			},
			rules: {
				requiredImagesInput: (v: any) => {
					let numImages = 0;
					if (Array.isArray(v)) {
						numImages = v.length;
					} else {
						numImages = (v?.uploads?.length || 0) + (v?.nodeRunResults?.length || 0);
					}
					return (numImages > 0) || 'At least one image is required';
				},
			},
			annotateSessionImgsSnapshot: null as RunnitNodeRunInputValue | null,
		};
	},
	destroyed () {
		this.exitSelectionMode(this.selectionStateTriggerId);
	},
	computed: {
		...mapState([
			'runnitState',
			'user',
		]),
		getVisualPromptEditorState () {
			const states = this.runnitState.visualPromptEditorStates;
			return new Proxy(states, {
				get: (target, prop: string) => {
					return target[prop] || {
						open: false,
						saving: false,
						backgroundImageUrl: null,
						replaceImageTarget: null,
					};
				},
			});
		},
		getPose3DEditorState () {
			const states = this.runnitState.pose3DEditorStates;
			return new Proxy(states, {
				get: (target, prop: string) => {
					return target[prop] || {
						open: false,
						saving: false,
						assetFile: null,
						assetName: null,
					};
				},
			});
		},
		selectionStateTriggerId () {
			return `multi-image-input-${this.field.uuid}`;
		},
		maxImages (): number {
			return this._get(this.field, 'display.maxImages') || null;
		},
		imagesValue (): any[] {
			const uploads = this._get(this.value, 'uploads') || [];
			const nodeRunResults = this._get(this.value, 'nodeRunResults') || [];
			return [...uploads, ...nodeRunResults];
		},
		/** Original for the image currently being annotated (Selected Images dialog). */
		visualPromptOriginalImage () {
			const state = this.getVisualPromptEditorState[this.field.uuid];
			if (!state || !state.replaceImageTarget) {
				return null;
			}
			return originalEntryForTarget(this.value, state.replaceImageTarget);
		},
		annotateSessionOpenForField (): boolean {
			return !!this.getVisualPromptEditorState[this.field.uuid]?.open;
		},
		selectionStateForTrigger () {
			return this.getSelectionStateByTrigger(this.selectionStateTriggerId);
		},
		visualPromptResetRequest () {
			return this.getVisualPromptEditorState[this.field.uuid]?.resetToOriginalRequest || null;
		},
	},
	watch: {
		annotateSessionOpenForField (isOpen: boolean, wasOpen: boolean) {
			if (isOpen && !wasOpen) {
				this.annotateSessionImgsSnapshot = _cloneDeep(this.value);
			}
		},
		visualPromptResetRequest (target) {
			if (!target) return;
			// Clear first so a re-render can't replay it, then reset without opening the editor.
			this.updateVisualPromptEditorState({
				fieldUuid: this.field.uuid,
				editorState: { resetToOriginalRequest: null },
			});
			this.resetImgsTarget(target, { refreshOpenEditor: false });
		},
		selectionStateForTrigger: {
			immediate: false,
			handler (newVal, oldVal) {
				const committedSelection = this.getCommittedMultiSelectionState(newVal, oldVal, this.field.uuid);
				if (!committedSelection) return;

				const { selectedNodeRunResults, selectedUploads } = committedSelection;

				// Enforce maxImages hard cap
				if (this.maxImages) {
					const totalCount = selectedUploads.length + selectedNodeRunResults.length;
					if (totalCount > this.maxImages) {
						if (this.selectFromLeftSessionActive) {
							// Restore last valid staged selection (do NOT revert to committed value)
							const baseState = newVal;
							this.updateSelectionState({
								...baseState,
								selectedUploads: [...this.lastValidSelectFromLeft.uploads],
								selectedNodeRunResults: [...this.lastValidSelectFromLeft.nodeRunResults],
								selectedNodeRunsMap: this.buildSelectedNodeRunsMapFromResults(this.lastValidSelectFromLeft.nodeRunResults),
							});
						} else {
							// Non-staged flows (e.g. Library dialog): revert to committed value
							this.syncSelectionState();
						}
						// Show snackbar message
						this.updateSnackbar({
							status: SNACKBAR_STATUS.ERROR,
							message: `Maximum of ${this.maxImages} images allowed`,
							show: true,
						});
						return;
					}
				}

				// While selecting-from-left, stage selections (keep committed value stable so UI doesn't flip)
				if (this.selectFromLeftSessionActive) {
					this.lastValidSelectFromLeft = {
						uploads: [...selectedUploads],
						nodeRunResults: [...selectedNodeRunResults],
					};
					return;
				}

				// Default behavior (Library dialog, etc.): commit immediately
				this.onInput({
					uploads: selectedUploads,
					nodeRunResults: selectedNodeRunResults,
				});
			},
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'updateSnackbar',
			'updateVisualPromptEditorState',
		]),
		onInput (value: any) {
			if (value && (value.uploads?.length > 0 || value.nodeRunResults?.length > 0)) {
				const uploads = value.uploads || [];
				const nodeRunResults = value.nodeRunResults || [];
				const originalImages = buildOriginalImagesMap(
					uploads,
					nodeRunResults,
					value.originalImages || this._get(this.value, 'originalImages') || {},
				);
				this.$emit('input', {
					...value,
					originalImages,
				});
			} else {
				this.$emit('input', null);
			}
		},
		clearValue () {
			this.onInput(null);
			this.clearSelectionsFromState(this.selectionStateTriggerId);
			this.exitSelectionMode(this.selectionStateTriggerId);
			this.selectFromLeftSessionActive = false;
			this.lastValidSelectFromLeft = { uploads: [], nodeRunResults: [] };
		},
		/**
		 * @param explicit — use after $emit when `this.value` may still reflect the prior prop until parent re-renders.
		 */
		syncSelectionState (explicit?: { uploads: RunnitUpload[]; nodeRunResults: RunnitNodeRunResult[]; originalImages?: any }) {
			const currentUploads = explicit
				? explicit.uploads
				: (this._get(this.value, 'uploads') || []);
			const currentNodeRunResults = explicit
				? explicit.nodeRunResults
				: (this._get(this.value, 'nodeRunResults') || []);
			const originalImages = explicit && explicit.originalImages !== undefined
				? explicit.originalImages
				: (this._get(this.value, 'originalImages') || {});
			const existing = this.getSelectionStateByTrigger(this.selectionStateTriggerId) || {};
			const keepSelectionActive = !!(
				this.runnitState.selectedImagesDialogOpen ||
				this.currentSelectionState?.triggeringId === this.selectionStateTriggerId
			);
			const sortOrder = keepSelectionActive
				? this.getNextPriorityNumber(this.selectionStateTriggerId)
				: (existing.sortOrder || 0);

			this.syncImgsSelectionState({
				triggeringId: this.selectionStateTriggerId,
				fieldAwaitingImageGallerySelection: this.field,
				uploads: currentUploads,
				nodeRunResults: currentNodeRunResults,
				keepActive: sortOrder > 0,
				originalImages,
			});
		},
		startLibrarySelectionMode (imageGalleryDialogInitialNav: ImageGalleryDialogNav) {
			// Sync selection state with current component value before opening library
			// This ensures removed images don't reappear when selecting new ones
			this.syncSelectionState();
			this.updateRunnitState({
				imageGalleryDialogOpen: true,
				imageGalleryDialogInitialNav: imageGalleryDialogInitialNav,
			});
			this.enterSelectionMode(NODE_RUN_RESULT_SELECTION_MODE.MULTIPLE, this.selectionStateTriggerId, this.field, ['IMG']);
		},
		openSelectedImagesDialog () {
			this.syncSelectionState();
			this.updateRunnitState({ selectedImagesDialogOpen: true });
			this.enterSelectionMode(NODE_RUN_RESULT_SELECTION_MODE.MULTIPLE, this.selectionStateTriggerId, this.field, ['IMG']);
		},
		onToggleImageSelectionMode () {
			if (this.currentSelectionState?.triggeringId === this.selectionStateTriggerId) {
				// Exiting "select from left": commit staged selection once
				if (this.selectFromLeftSessionActive) {
					const state: any = this.getSelectionStateByTrigger(this.selectionStateTriggerId);
					const selectedUploads: RunnitUpload[] = this._get(state, 'selectedUploads') || [];
					const selectedNodeRunResults: RunnitNodeRunResult[] = this._get(state, 'selectedNodeRunResults') || [];
					this.onInput({
						uploads: selectedUploads,
						nodeRunResults: selectedNodeRunResults,
					});
				}
				this.exitSelectionMode(this.selectionStateTriggerId);
				this.selectFromLeftSessionActive = false;
				this.lastValidSelectFromLeft = { uploads: [], nodeRunResults: [] };
			} else {
				// Entering "select from left": stage selection changes (don't commit until exit)
				this.selectFromLeftSessionActive = true;
				this.lastValidSelectFromLeft = {
					uploads: [...(this._get(this.value, 'uploads') || [])],
					nodeRunResults: [...(this._get(this.value, 'nodeRunResults') || [])],
				};
				this.syncSelectionState();
				this.enterSelectionMode(NODE_RUN_RESULT_SELECTION_MODE.MULTIPLE, this.selectionStateTriggerId, this.field, ['IMG']);
			}
		},
		handleImageUpload (items: any[]) {
			// Filter out null items
			const validItems = items.filter(item => !!item);

			const newUploads = [];
			const newNodeRunResults = [];

			validItems.forEach(item => {
				if (item.nodeRunResult) {
					// It's a mapped nodeRunResult from drag and drop
					newNodeRunResults.push(item.nodeRunResult);
				} else if (item.upload) {
					// It's a mapped upload from drag and drop
					newUploads.push(item.upload);
				} else {
					// It's a raw UploadFile (RunnitUpload) from file selection/drop
					newUploads.push(item);
				}
			});

			const currentUploads = this._get(this.value, 'uploads') || [];
			const currentNodeRunResults = this._get(this.value, 'nodeRunResults') || [];

			let combinedUploads = [...currentUploads, ...newUploads];
			let combinedNodeRunResults = [...currentNodeRunResults, ...newNodeRunResults];

			if (this.maxImages) {
				const totalCount = combinedUploads.length + combinedNodeRunResults.length;
				if (totalCount > this.maxImages) {
					// Create a ordered list of all images to truncate correctly
					// Note: this maintains the existing order (uploads then results) which matches allImages computed prop
					const allImages = [
						...combinedUploads.map(u => ({ type: 'upload', data: u })),
						...combinedNodeRunResults.map(r => ({ type: 'result', data: r })),
					];

					const truncated = allImages.slice(0, this.maxImages);
					combinedUploads = truncated.filter(i => i.type === 'upload').map(i => i.data);
					combinedNodeRunResults = truncated.filter(i => i.type === 'result').map(i => i.data);

					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Maximum of ${this.maxImages} images allowed`,
						show: true,
					});
				}
			}

			this.onInput({
				uploads: combinedUploads,
				nodeRunResults: combinedNodeRunResults,
			});

			this.clearAllSelectionStates();
		},
		removeImage (index: number) {
			const uploads = this._get(this.value, 'uploads') || [];
			const nodeRunResults = this._get(this.value, 'nodeRunResults') || [];
			const allImages = [...uploads, ...nodeRunResults];

			// Remove the image at the specified index
			allImages.splice(index, 1);

			// Separate back into uploads and nodeRunResults
			const newUploads = allImages.filter((img: any) => !img.uuid); // uploads don't have uuid
			const newNodeRunResults = allImages.filter((img: any) => img.uuid); // nodeRunResults have uuid

			this.onInput({
				uploads: newUploads,
				nodeRunResults: newNodeRunResults,
			});
		},
		validateMaxImages (v: RunnitNodeRunInputValue) {
			if (!this.maxImages) return true;
			const numImages: number = (v?.uploads?.length || 0) + (v?.nodeRunResults?.length || 0);
			return numImages <= this.maxImages || `Maximum of ${this.maxImages} images allowed`;
		},
		async onVisualPromptResetToOriginal () {
			const target = this.getVisualPromptEditorState[this.field.uuid]?.replaceImageTarget;
			if (!target) return;
			await this.resetImgsTarget(target, { refreshOpenEditor: true });
		},
		/**
		 * Reset one image to its original. Shared by the in-editor Reset button
		 * (refreshOpenEditor: shows the restored original in the still-open editor) and the
		 * Selected Images dialog's per-tile Reset (no editor involved).
		 */
		async resetImgsTarget (target, { refreshOpenEditor }: { refreshOpenEditor: boolean }) {
			const next = resetImgsValueAtTarget(this.value, target);
			if (!next) return;

			this.onInput({
				uploads: next.uploads,
				nodeRunResults: next.nodeRunResults,
				originalImages: next.originalImages,
			});

			await this.$nextTick();
			this.syncSelectionState({
				uploads: next.uploads,
				nodeRunResults: next.nodeRunResults,
				originalImages: next.originalImages,
			});

			if (!refreshOpenEditor) return;
			const source = originalImageSourceFromEntry(next.original);
			if (!source) return;
			const baseSource = annotationSourceReference(source.source, source.useCase);
			if (!baseSource) {
				this.updateVisualPromptEditorState({ fieldUuid: this.field.uuid, editorState: { open: false, backgroundImageUrl: null, baseSource: null } });
				return;
			}
			const { imageUrl } = await fetchRunnitImageSrc(source.source, source.useCase);
			this.updateVisualPromptEditorState({
				fieldUuid: this.field.uuid,
				editorState: {
					open: true,
					backgroundImageUrl: imageUrl,
					baseSource,
				},
			});
		},
		onVisualPromptAnnotateCancel () {
			if (this.annotateSessionImgsSnapshot == null) return;
			const snap = _cloneDeep(this.annotateSessionImgsSnapshot);
			this.onInput(snap);
			this.$nextTick(() => {
				this.syncSelectionState({
					uploads: this._get(snap, 'uploads') || [],
					nodeRunResults: this._get(snap, 'nodeRunResults') || [],
					originalImages: this._get(snap, 'originalImages') || {},
				});
			});
			this.annotateSessionImgsSnapshot = null;
		},
		onVisualPromptAnnotateSubmitDismissed () {
			this.annotateSessionImgsSnapshot = null;
		},
		handleImageReplacement ({ upload, replaceImageTarget }: { upload: RunnitUpload, replaceImageTarget: ImgsVisualPromptReplaceTarget }) {
			const next = replaceImgsValueAtTarget(this.value, replaceImageTarget, upload);
			if (!next) return;

			this.onInput({
				uploads: next.uploads,
				nodeRunResults: next.nodeRunResults,
				originalImages: next.originalImages,
			});

			this.$nextTick(() => {
				this.syncSelectionState({
					uploads: next.uploads,
					nodeRunResults: next.nodeRunResults,
					originalImages: next.originalImages,
				});
				this.updateVisualPromptEditorState({
					fieldUuid: this.field.uuid,
					editorState: {
						completedReplacement: {
							upload,
							replaceImageTarget,
						},
					},
				});
				this.annotateSessionImgsSnapshot = null;
			});
		},
		onPose3DCapture (imageValue: { upload?: RunnitUpload }) {
			if (!imageValue || !imageValue.upload) return;
			// Pose3D capture appends a single RunnitUpload to the IMGS array; reuse handleImageUpload.
			this.handleImageUpload([{ upload: imageValue.upload }]);
		},
	},
	components: {
		MultiImageInputSelectedImageContainer,
		RunnitDynamicFieldLabelRow,
		VisualPromptEditor,
		Pose3DEditor,
	},
});
